import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import API_CONFIG from '../config/api.js';
import { getAuthorizedCompanyContext } from '../utils/companyContext.js';
import { showCompanySuspensionNotice } from '../utils/companySuspensionNotice.js';
import { installAxiosGlobalLoading } from '../utils/globalLoading.js';
import {
  HREmployee,
  HRDepartment,
  HRPosition,
  HRContract,
  HRContractType,
  HRPayrollItem,
  HREmployeePayrollItem,
  EmployeeFormData,
  DepartmentFormData,
  PositionFormData,
  ContractFormData,
  ContractTypeFormData,
  PayrollItemFormData,
  EmployeePayrollItemFormData,
  APIResponse,
} from "../types/hr";

const hrApi = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}/api/hr`,
  timeout: 10000,
});

installAxiosGlobalLoading(hrApi);

// Intercepteur pour ajouter le token d'authentification à chaque requête
hrApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      const companyContextId = getAuthorizedCompanyContext();
      if (companyContextId) {
        config.headers["X-Company-Id"] = String(companyContextId);
      }
    }
    // Pour les FormData, axios définit le Content-Type automatiquement.
    // Pour les autres, on s'assure qu'il est bien en json.
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }


    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de manière globale
hrApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {

    const responseData = error.response?.data;
    const message =
      responseData?.message || error.message || "Une erreur est survenue";
    const errorCode = responseData?.code || responseData?.error_code;

    if (error.response?.status === 403 && errorCode === 'COMPANY_SUSPENDED') {
      showCompanySuspensionNotice(responseData);
      return Promise.reject(error);
    }

    // Gestion spécifique des erreurs d'assignation
    if (
      error.config?.url?.includes("/employee/") &&
      error.config?.method === "post"
    ) {
      if (
        errorCode === "DUPLICATE_ASSIGNMENT" ||
        error.response?.status === 409
      ) {
        toast.error("Cet élément de paie est déjà assigné à l'employé");
        return Promise.reject(
          new Error("Cet élément de paie est déjà assigné à l'employé")
        );
      }

      if (errorCode === "INVALID_REFERENCE") {
        toast.error(
          "Référence invalide : employé ou élément de paie introuvable"
        );
        return Promise.reject(new Error("Référence invalide"));
      }
    }

    // Gestion spéciale pour l'expiration de session admin
    if (error.response?.status === 401 && errorCode === "ADMIN_SESSION_EXPIRED") {
      // Force logout et redirection immédiate
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      window.location.href = '/login?reason=admin_session_expired';
      return Promise.reject(error);
    }

    if (error.code === "ERR_NETWORK") {
      toast.error("Erreur de connexion au serveur");
    } else if (error.response?.status === 400) {
      toast.error(`Erreur de validation: ${message}`);
    } else if (error.response?.status === 403) {
      toast.error("Accès refusé: Permissions insuffisantes");
    } else if (error.response?.status === 404) {
      toast.error("Ressource non trouvée");
    } else if (error.response?.status === 409) {
      toast.error(`Conflit: ${message}`);
    } else if (error.response?.status === 500) {
      if (responseData?.debug && process.env.NODE_ENV === "development") {
        toast.error(`Erreur serveur: ${responseData.debug}`);
      } else {
        toast.error("Erreur interne du serveur");
      }
    } else {
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

class HRAPIService {
  // ===== EMPLOYÉS =====

  async getEmployees(): Promise<HREmployee[]> {
    const response = await hrApi.get("/employees");
    // CORRECTION: Accéder correctement aux données de la réponse
    const data: APIResponse<HREmployee[]> = response.data;
    return data.data || [];
  }

  async getEmployee(id: number): Promise<HREmployee> {
    const response = await hrApi.get(`/employees/${id}`);
    const data: APIResponse<HREmployee> = response.data;
    if (!data.data) throw new Error("Employé non trouvé");
    return data.data;
  }

  async createEmployee(data: EmployeeFormData): Promise<HREmployee> {

    // CORRECTION: Créer FormData seulement si nécessaire
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (value !== null && value !== undefined && value !== "") {
        formData.append(key, String(value));
      }
    });


    const response = await hrApi.post("/employees", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const responseData: APIResponse<HREmployee> = response.data;

    if (!responseData.data) throw new Error("Erreur lors de la création");
    return responseData.data;
  }

  async updateEmployee(
    id: number,
    data: Partial<EmployeeFormData>
  ): Promise<HREmployee> {

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if ((value as any) instanceof File) {
        formData.append(key, value as unknown as File);
      } else if (value !== null && value !== undefined && value !== "") {
        formData.append(key, String(value));
      }
    });


    const response = await hrApi.put(`/employees/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const responseData: APIResponse<HREmployee> = response.data;
    console.log("[HR_API] Update employee response:", responseData);

    if (!responseData.data) throw new Error("Erreur lors de la mise à jour");
    return responseData.data;
  }

  async deleteEmployee(id: number): Promise<void> {
    await hrApi.delete(`/employees/${id}`);
  }

  // ===== DÉPARTEMENTS =====

  async getDepartments(): Promise<HRDepartment[]> {
    const response = await hrApi.get("/departments");
    const data: APIResponse<HRDepartment[]> = response.data;
    return data.data || [];
  }

  async getDepartment(id: number): Promise<HRDepartment> {
    const response = await hrApi.get(`/departments/${id}`);
    const data: APIResponse<HRDepartment> = response.data;
    if (!data.data) throw new Error("Département non trouvé");
    return data.data;
  }

  async createDepartment(data: DepartmentFormData): Promise<HRDepartment> {
    console.log("[HR_API] Creating department with data:", data);

    const response = await hrApi.post("/departments", data);
    const responseData: APIResponse<HRDepartment> = response.data;

    console.log("[HR_API] Create department response:", responseData);

    if (!responseData.data) throw new Error("Erreur lors de la création");
    return responseData.data;
  }

  async updateDepartment(
    id: number,
    data: Partial<DepartmentFormData>
  ): Promise<void> {
    await hrApi.put(`/departments/${id}`, data);
  }

  async deleteDepartment(id: number): Promise<void> {
    await hrApi.delete(`/departments/${id}`);
  }

  // ===== POSTES =====

  async getPositions(): Promise<HRPosition[]> {
    const response = await hrApi.get("/positions");
    const data: APIResponse<HRPosition[]> = response.data;
    return data.data || [];
  }

  async getPosition(id: number): Promise<HRPosition> {
    const response = await hrApi.get(`/positions/${id}`);
    const data: APIResponse<HRPosition> = response.data;
    if (!data.data) throw new Error("Poste non trouvé");
    return data.data;
  }

  async getPositionsByDepartment(departmentId: number): Promise<HRPosition[]> {
    const response = await hrApi.get(
      `/positions/by-department/${departmentId}`
    );
    const data: APIResponse<HRPosition[]> = response.data;
    return data.data || [];
  }

  async createPosition(data: PositionFormData): Promise<HRPosition> {
    const response = await hrApi.post("/positions", data);
    const responseData: APIResponse<{ id: number }> = response.data;
    if (!responseData.data?.id) throw new Error("Erreur lors de la création");
    return this.getPosition(responseData.data.id);
  }

  async updatePosition(
    id: number,
    data: Partial<PositionFormData>
  ): Promise<void> {
    await hrApi.put(`/positions/${id}`, data);
  }

  async deletePosition(id: number): Promise<void> {
    await hrApi.delete(`/positions/${id}`);
  }

  // ===== CONTRATS =====

  async getContracts(filters?: Record<string, any>): Promise<HRContract[]> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, String(value));
        }
      });
    }
    const endpoint = `/contracts?${queryParams.toString()}`;
    const response = await hrApi.get(endpoint);
    const data: APIResponse<HRContract[]> = response.data;
    return data.data || [];
  }

  async getContract(id: number): Promise<HRContract> {
    const response = await hrApi.get(`/contracts/${id}`);
    const data: APIResponse<HRContract> = response.data;
    if (!data.data) throw new Error("Contrat non trouvé");
    return data.data;
  }

  async getExpiringContracts(days: number = 30): Promise<HRContract[]> {
    const response = await hrApi.get(`/contracts/expiring?days=${days}`);
    const data: APIResponse<HRContract[]> = response.data;
    return data.data || [];
  }

  async createContract(data: ContractFormData): Promise<HRContract> {
    // Si des fichiers sont présents, utiliser FormData
    const hasFiles =
      data.contract_file instanceof File ||
      data.signed_contract_file instanceof File;

    let requestData;
    if (hasFiles) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (value !== null && value !== undefined && value !== "") {
          formData.append(key, String(value));
        }
      });
      requestData = formData;
    } else {
      requestData = data;
    }

    const response = await hrApi.post("/contracts", requestData, {
      headers: hasFiles ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    const responseData: APIResponse<{ id: number }> = response.data;
    if (!responseData.data?.id) throw new Error("Erreur lors de la création");
    return this.getContract(responseData.data.id);
  }

  async updateContract(
    id: number,
    data: Partial<ContractFormData>
  ): Promise<void> {
    // Si des fichiers sont présents, utiliser FormData
    const hasFiles =
      data.contract_file instanceof File ||
      data.signed_contract_file instanceof File;

    let requestData;
    if (hasFiles) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (value !== null && value !== undefined && value !== "") {
          formData.append(key, String(value));
        }
      });
      requestData = formData;
    } else {
      requestData = data;
    }

    await hrApi.put(`/contracts/${id}`, requestData, {
      headers: hasFiles ? { "Content-Type": "multipart/form-data" } : undefined,
    });
  }

  async deleteContract(id: number): Promise<void> {
    await hrApi.delete(`/contracts/${id}`);
  }

  async renewContract(
    id: number,
    data: {
      new_end_date: string;
      new_salary?: number;
      terms_and_conditions?: string;
    }
  ): Promise<void> {
    await hrApi.post(`/contracts/${id}/renew`, data);
  }

  async getContractTypes(): Promise<HRContractType[]> {
    const response = await hrApi.get("/contracts/contract-types");
    const data: APIResponse<HRContractType[]> = response.data;
    return data.data || [];
  }

  async createContractType(
    data: ContractTypeFormData
  ): Promise<HRContractType> {
    console.log("[HR_API] Creating contract type with data:", data);

    const response = await hrApi.post("/contracts/contract-types", data);
    const responseData: APIResponse<HRContractType> = response.data;

    console.log("[HR_API] Create contract type response:", responseData);

    if (!responseData.data) throw new Error("Erreur lors de la création");
    return responseData.data;
  }

  async updateContractType(
    id: number,
    data: Partial<ContractTypeFormData>
  ): Promise<void> {
    console.log("[HR_API] Updating contract type with data:", data);

    const response = await hrApi.put(`/contracts/contract-types/${id}`, data);
    const responseData: APIResponse<any> = response.data;

    console.log("[HR_API] Update contract type response:", responseData);

    if (!responseData.success) {
      throw new Error(responseData.message || "Erreur lors de la mise à jour");
    }
  }

  async deleteContractType(id: number): Promise<void> {
    await hrApi.delete(`/contracts/contract-types/${id}`);
  }

  async getCurrencies(): Promise<any[]> {
    try {
      // Currencies endpoint is at /api/currencies, not /api/hr/currencies
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/currencies`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.data || [];
    } catch (error) {
      console.error("[HR_API] Error fetching currencies:", error);
      throw error;
    }
  }

  // ===== ÉLÉMENTS DE PAIE =====

  async getPayrollItems(): Promise<HRPayrollItem[]> {
    const response = await hrApi.get("/payroll-items");
    const data: APIResponse<HRPayrollItem[]> = response.data;
    return data.data || [];
  }

  async getPayrollItem(id: number): Promise<HRPayrollItem> {
    const response = await hrApi.get(`/payroll-items/${id}`);
    const data: APIResponse<HRPayrollItem> = response.data;
    if (!data.data) throw new Error("Élément de paie non trouvé");
    return data.data;
  }

  async createPayrollItem(data: PayrollItemFormData): Promise<HRPayrollItem> {
    console.log("[HR_API] Creating payroll item with data:", data);

    const response = await hrApi.post("/payroll-items", data);
    const responseData: APIResponse<HRPayrollItem> = response.data;

    console.log("[HR_API] Create payroll item response:", responseData);

    if (!responseData.data) throw new Error("Erreur lors de la création");
    return responseData.data;
  }

  async updatePayrollItem(
    id: number,
    data: Partial<PayrollItemFormData>
  ): Promise<void> {
    console.log("[HR_API] Updating payroll item with data:", data);

    const response = await hrApi.put(`/payroll-items/${id}`, data);
    const responseData: APIResponse<void> = response.data;

    console.log("[HR_API] Update payroll item response:", responseData);

    if (!responseData.success) throw new Error("Erreur lors de la mise à jour");
  }

  async deletePayrollItem(id: number): Promise<void> {
    await hrApi.delete(`/payroll-items/${id}`);
  }

  async createDefaultPayrollItems(): Promise<void> {
    const response = await hrApi.post("/payroll-items/defaults");
    const responseData: APIResponse<void> = response.data;

    if (!responseData.success)
      throw new Error("Erreur lors de la création des éléments par défaut");
  }

  // ===== AVANTAGES EN NATURE =====

  async getNaturaBenefits(employeeId: number, companyId: number): Promise<any> {
    const response = await hrApi.get(`/natura-benefits/employee/${employeeId}/${companyId}`);
    return response.data;
  }

  async calculateFiscalPreview(employeeId: number, companyId: number, basicSalary: number, sector: string): Promise<any> {
    const response = await hrApi.post(`/natura-benefits/fiscal-impact/${employeeId}/${companyId}`, {
      basicSalary,
      sector
    });
    return response.data;
  }

  async createNaturaBenefit(employeeId: number, benefitData: any): Promise<any> {
    const response = await hrApi.post(`/natura-benefits/employee/${employeeId}`, benefitData);
    return response.data;
  }

  async updateNaturaBenefit(benefitId: number, benefitData: any): Promise<any> {
    const response = await hrApi.put(`/natura-benefits/${benefitId}`, benefitData);
    return response.data;
  }

  async deleteNaturaBenefit(benefitId: number, companyId: number): Promise<any> {
    const response = await hrApi.delete(`/natura-benefits/${benefitId}`, {
      params: { company_id: companyId },
    });
    return response.data;
  }

  // ===== ÉLÉMENTS DE PAIE DES EMPLOYÉS =====

  async getEmployeePayrollItems(
    employeeId: number
  ): Promise<HREmployeePayrollItem[]> {
    const response = await hrApi.get(`/payroll-items/employee/${employeeId}`);
    const data: APIResponse<HREmployeePayrollItem[]> = response.data;
    return data.data || [];
  }

  async assignPayrollItemToEmployee(
    employeeId: number,
    data: EmployeePayrollItemFormData
  ): Promise<HREmployeePayrollItem> {
    console.log("[HR_API] Assigning payroll item to employee:", {
      employeeId,
      data,
    });

    // Validation côté client avant envoi
    if (!employeeId || isNaN(employeeId)) {
      throw new Error("ID employé invalide");
    }

    if (!data.payroll_item_id || isNaN(data.payroll_item_id)) {
      throw new Error("Élément de paie invalide");
    }

    // Validation des montants
    if (data.custom_amount !== undefined && data.custom_amount !== null) {
      const amount = Number(data.custom_amount);
      if (!Number.isFinite(amount) || amount < 0) {
        throw new Error("Le montant personnalisé doit être un nombre positif");
      }
    }

    if (
      data.custom_percentage !== undefined &&
      data.custom_percentage !== null
    ) {
      const percentage = Number(data.custom_percentage);
      if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
        throw new Error("Le pourcentage doit être entre 0 et 100");
      }
    }

    // Nettoyer les données avant envoi
    const cleanData = {
      payroll_item_id: parseInt(String(data.payroll_item_id)),
      custom_amount: data.custom_amount == null ? null : Number(data.custom_amount),
      custom_percentage: data.custom_percentage == null ? null : Number(data.custom_percentage),
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      is_active: data.is_active !== false, // Default to true
    };

    console.log("[HR_API] Cleaned data for assignment:", cleanData);

    try {
      const response = await hrApi.post(
        `/payroll-items/employee/${employeeId}`,
        cleanData
      );
      const responseData: APIResponse<HREmployeePayrollItem> = response.data;

      console.log("[HR_API] Assign payroll item response:", responseData);

      if (!responseData.success) {
        throw new Error(responseData.message || "Erreur lors de l'assignation");
      }

      if (!responseData.data) {
        throw new Error("Données de réponse manquantes");
      }

      return responseData.data;
    } catch (error: any) {
      console.error("[HR_API] Assignment error:", {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        employeeId,
        data: cleanData,
      });

      // Re-lancer l'erreur pour que l'intercepteur la gère
      throw error;
    }
  }

  async updateEmployeePayrollItem(
    employeeId: number,
    itemId: number,
    data: Partial<EmployeePayrollItemFormData>
  ): Promise<void> {
    console.log("[HR_API] Updating employee payroll item:", {
      employeeId,
      itemId,
      data,
    });

    const response = await hrApi.put(
      `/payroll-items/employee/${employeeId}/${itemId}`,
      data
    );
    const responseData: APIResponse<void> = response.data;

    console.log(
      "[HR_API] Update employee payroll item response:",
      responseData
    );

    if (!responseData.success) throw new Error("Erreur lors de la mise à jour");
  }

  async removePayrollItemFromEmployee(
    employeeId: number,
    itemId: number
  ): Promise<void> {
    await hrApi.delete(`/payroll-items/employee/${employeeId}/${itemId}`);
  }
}

const hrAPIService = new HRAPIService();

export const useHRAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withErrorHandling = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await operation();
        return result;
      } catch (err) {
        console.error("[HR_API] Error in operation:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Une erreur est survenue";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => setError(null), []);

  // Le reste des fonctions (createEmployee, getEmployees, etc.) reste le même,
  // mais elles utiliseront maintenant l'instance axios via hrAPIService.

  const getEmployees = useCallback(
    () => withErrorHandling(() => hrAPIService.getEmployees()),
    [withErrorHandling]
  );
  const getEmployee = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.getEmployee(id)),
    [withErrorHandling]
  );
  const createEmployee = useCallback(
    (data: EmployeeFormData) =>
      withErrorHandling(() => hrAPIService.createEmployee(data)),
    [withErrorHandling]
  );
  const updateEmployee = useCallback(
    (id: number, data: Partial<EmployeeFormData>) =>
      withErrorHandling(() => hrAPIService.updateEmployee(id, data)),
    [withErrorHandling]
  );
  const deleteEmployee = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.deleteEmployee(id)),
    [withErrorHandling]
  );

  const getDepartments = useCallback(
    () => withErrorHandling(() => hrAPIService.getDepartments()),
    [withErrorHandling]
  );
  const getDepartment = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.getDepartment(id)),
    [withErrorHandling]
  );
  const createDepartment = useCallback(
    (data: DepartmentFormData) =>
      withErrorHandling(() => hrAPIService.createDepartment(data)),
    [withErrorHandling]
  );
  const updateDepartment = useCallback(
    (id: number, data: Partial<DepartmentFormData>) =>
      withErrorHandling(() => hrAPIService.updateDepartment(id, data)),
    [withErrorHandling]
  );
  const deleteDepartment = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.deleteDepartment(id)),
    [withErrorHandling]
  );

  const getPositions = useCallback(
    () => withErrorHandling(() => hrAPIService.getPositions()),
    [withErrorHandling]
  );
  const getPosition = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.getPosition(id)),
    [withErrorHandling]
  );
  const getPositionsByDepartment = useCallback(
    (departmentId: number) =>
      withErrorHandling(() =>
        hrAPIService.getPositionsByDepartment(departmentId)
      ),
    [withErrorHandling]
  );
  const createPosition = useCallback(
    (data: PositionFormData) =>
      withErrorHandling(() => hrAPIService.createPosition(data)),
    [withErrorHandling]
  );
  const updatePosition = useCallback(
    (id: number, data: Partial<PositionFormData>) =>
      withErrorHandling(() => hrAPIService.updatePosition(id, data)),
    [withErrorHandling]
  );
  const deletePosition = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.deletePosition(id)),
    [withErrorHandling]
  );

  const getContracts = useCallback(
    (filters?: Record<string, any>) =>
      withErrorHandling(() => hrAPIService.getContracts(filters)),
    [withErrorHandling]
  );
  const getContract = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.getContract(id)),
    [withErrorHandling]
  );
  const getExpiringContracts = useCallback(
    (days?: number) =>
      withErrorHandling(() => hrAPIService.getExpiringContracts(days)),
    [withErrorHandling]
  );
  const createContract = useCallback(
    (data: ContractFormData) =>
      withErrorHandling(() => hrAPIService.createContract(data)),
    [withErrorHandling]
  );
  const updateContract = useCallback(
    (id: number, data: Partial<ContractFormData>) =>
      withErrorHandling(() => hrAPIService.updateContract(id, data)),
    [withErrorHandling]
  );
  const deleteContract = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.deleteContract(id)),
    [withErrorHandling]
  );
  const renewContract = useCallback(
    (
      id: number,
      data: {
        new_end_date: string;
        new_salary?: number;
        terms_and_conditions?: string;
      }
    ) => withErrorHandling(() => hrAPIService.renewContract(id, data)),
    [withErrorHandling]
  );

  const getContractTypes = useCallback(
    () => withErrorHandling(() => hrAPIService.getContractTypes()),
    [withErrorHandling]
  );
  const createContractType = useCallback(
    (data: ContractTypeFormData) =>
      withErrorHandling(() => hrAPIService.createContractType(data)),
    [withErrorHandling]
  );
  const updateContractType = useCallback(
    (id: number, data: Partial<ContractTypeFormData>) =>
      withErrorHandling(() => hrAPIService.updateContractType(id, data)),
    [withErrorHandling]
  );
  const deleteContractType = useCallback(
    (id: number) =>
      withErrorHandling(() => hrAPIService.deleteContractType(id)),
    [withErrorHandling]
  );

  const getCurrencies = useCallback(
    () => withErrorHandling(() => hrAPIService.getCurrencies()),
    [withErrorHandling]
  );

  // ===== ÉLÉMENTS DE PAIE =====

  const getPayrollItems = useCallback(
    () => withErrorHandling(() => hrAPIService.getPayrollItems()),
    [withErrorHandling]
  );

  const getPayrollItem = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.getPayrollItem(id)),
    [withErrorHandling]
  );

  const createPayrollItem = useCallback(
    (data: PayrollItemFormData) =>
      withErrorHandling(() => hrAPIService.createPayrollItem(data)),
    [withErrorHandling]
  );

  const updatePayrollItem = useCallback(
    (id: number, data: Partial<PayrollItemFormData>) =>
      withErrorHandling(() => hrAPIService.updatePayrollItem(id, data)),
    [withErrorHandling]
  );

  const deletePayrollItem = useCallback(
    (id: number) => withErrorHandling(() => hrAPIService.deletePayrollItem(id)),
    [withErrorHandling]
  );

  const createDefaultPayrollItems = useCallback(
    () => withErrorHandling(() => hrAPIService.createDefaultPayrollItems()),
    [withErrorHandling]
  );

  // ===== ÉLÉMENTS DE PAIE DES EMPLOYÉS =====

  const getEmployeePayrollItems = useCallback(
    (employeeId: number) =>
      withErrorHandling(() => hrAPIService.getEmployeePayrollItems(employeeId)),
    [withErrorHandling]
  );

  const assignPayrollItemToEmployee = useCallback(
    (employeeId: number, data: EmployeePayrollItemFormData) =>
      withErrorHandling(() =>
        hrAPIService.assignPayrollItemToEmployee(employeeId, data)
      ),
    [withErrorHandling]
  );

  const updateEmployeePayrollItem = useCallback(
    (
      employeeId: number,
      itemId: number,
      data: Partial<EmployeePayrollItemFormData>
    ) =>
      withErrorHandling(() =>
        hrAPIService.updateEmployeePayrollItem(employeeId, itemId, data)
      ),
    [withErrorHandling]
  );

  const removePayrollItemFromEmployee = useCallback(
    (employeeId: number, itemId: number) =>
      withErrorHandling(() =>
        hrAPIService.removePayrollItemFromEmployee(employeeId, itemId)
      ),
    [withErrorHandling]
  );

  // ===== AVANTAGES EN NATURE =====

  const getNaturaBenefits = useCallback(
    (employeeId: number, companyId: number) =>
      withErrorHandling(() => hrAPIService.getNaturaBenefits(employeeId, companyId)),
    [withErrorHandling]
  );

  const calculateFiscalPreview = useCallback(
    (employeeId: number, companyId: number, basicSalary: number, sector: string) =>
      withErrorHandling(() => hrAPIService.calculateFiscalPreview(employeeId, companyId, basicSalary, sector)),
    [withErrorHandling]
  );

  const createNaturaBenefit = useCallback(
    (employeeId: number, benefitData: any) =>
      withErrorHandling(() => hrAPIService.createNaturaBenefit(employeeId, benefitData)),
    [withErrorHandling]
  );

  const updateNaturaBenefit = useCallback(
    (benefitId: number, benefitData: any) =>
      withErrorHandling(() => hrAPIService.updateNaturaBenefit(benefitId, benefitData)),
    [withErrorHandling]
  );

  const deleteNaturaBenefit = useCallback(
    (benefitId: number, companyId: number) =>
      withErrorHandling(() =>
        hrAPIService.deleteNaturaBenefit(benefitId, companyId)
      ),
    [withErrorHandling]
  );

  return {
    loading,
    error,
    clearError,
    getEmployees,
    getEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    getDepartments,
    getDepartment,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    getPositions,
    getPosition,
    getPositionsByDepartment,
    createPosition,
    updatePosition,
    deletePosition,
    getContracts,
    getContract,
    getExpiringContracts,
    createContract,
    updateContract,
    deleteContract,
    renewContract,
    getContractTypes,
    createContractType,
    updateContractType,
    deleteContractType,
    getCurrencies,
    // Éléments de paie
    getPayrollItems,
    getPayrollItem,
    createPayrollItem,
    updatePayrollItem,
    deletePayrollItem,
    createDefaultPayrollItems,
    // Éléments de paie des employés
    getEmployeePayrollItems,
    assignPayrollItemToEmployee,
    updateEmployeePayrollItem,
    removePayrollItemFromEmployee,
    // Avantages en nature
    getNaturaBenefits,
    calculateFiscalPreview,
    createNaturaBenefit,
    updateNaturaBenefit,
    deleteNaturaBenefit,
  };
};

export default hrAPIService;
